import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import OtherUserGroupModel, { OtherUserPermittedFunctions } from "@/commons/models/OtherUserGroup";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 施設関係者グループメンバー API 操作クラス
 */
export default class OtherUserGroupMembers {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET OtherUserGroupMembers
   */
  public async get(userStore: LoginUser, groupId?: number): Promise<Array<OtherUserGroupModel>> {
    let requestUrl: string = "";

    // 施設IDのみの場合のURL作成
    if (!groupId) {
      const formatParams: any = {
        facilityId: userStore.facility.id
      };
      requestUrl = UrlFormatter.format(RestUrl.OtherUserGroupMembersInFacility, formatParams);
    }
    // 施設ID、グループIDの場合のURL作成
    else if (groupId) {
      const formatParams: any = {
        facilityId: userStore.facility.id,
        groupId: groupId
      };
      requestUrl = UrlFormatter.format(RestUrl.OtherUserGroupMembers, formatParams);
    }

    const response = await this.commonService.executeGet<Array<OtherUserGroupModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    const result: Array<OtherUserGroupModel> = this.createGetResponse(response.data);

    return result;
  }

  /**
   * GETの返却用Model作成
   *
   * @param body レスポンスボディ
   */
  private createGetResponse(body: any): Array<OtherUserGroupModel> {
    // 配列で返したいので、配列でなければ格納しなおす
    let response: Array<OtherUserGroupModel> = [];

    if (Array.isArray(body)) {
      response = body;
    } else {
      response.push(body);
    }

    return response;
  }

  /**
   * PATCH OtherUserGroupMembers
   */
  public async patch(userStore: LoginUser, groupId: number, memberId: number, model: OtherUserGroupMembersPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId,
      memberId: memberId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroupMember, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, model, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE OtherUserGroupMembers
   */
  public async delete(userStore: LoginUser, groupId: number, memberId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId,
      memberId: memberId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroupMember, formatParams);
    // クエリパラメータを付与する
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, { "send-message": "true" }, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST OtherUserGroupMembers Transfer
   */
  public async postTransfer(userStore: LoginUser, groupId: number, memberId: number, requestBody: OtherUserGroupMemberTransferPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      groupId: groupId,
      memberId: memberId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroupMemberTransfer, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OtherUserGroupMembersPatchModel {
  /** 変更後グループID */
  groupId: number;
  /** メンバー名 */
  memberName: string;
  /** メンバー電話番号 */
  memberTel: string;
  /** メンバー組織名 */
  memberOrganization: string;
  /** メンバー最終メッセージ送信最終日時 */
  memberMessageLatestDate?: number;
  /** メンバー最終メッセージ */
  memberLatestMessage?: string;
  /** 施設関係者メンバー個別機能使用可否設定 */
  otherUserPermittedFunctions: OtherUserPermittedFunctions;
  /** 整合性チェック用更新日時 */
  updated: number;
}

export interface OtherUserGroupMemberTransferPostModel {
  /** 移動先施設ID */
  destinationFacilityId: number;

  /** 移動先施設関係者グループID */
  groupId: number;

  /** 移動後、現施設から削除するか否か */
  needDelete: boolean;
}
